import { motion, useReducedMotion } from "motion/react"
import { useEffect, useRef } from "react"

import { cn } from "@/lib/utils"

import { FOCUS_RING } from "./jacket-chrome"
import { chromeLabel, marginalia } from "./type-voices"
import type { JacketEntry } from "./use-jacket-feed"

/** The grace period before the page turns itself (rhymes with the photo dwell). */
export const UP_NEXT_GRACE_MS = 6000

/**
 * Contract for a candidate's Up Next panel (§7.3): a compact preview of the
 * next jacket in the candidate's own material — its poster and the first
 * line of its notes — with the UpNextRule drawing across its base.
 */
export interface UpNextSlotProps {
  next: JacketEntry
  nextIndex: number
  total: number
  onAdvance: () => void
  onStay: () => void
}

/**
 * The Up Next timer (§7.3): a 1px Ink rule that draws itself across the
 * base of the preview over the grace period. No digits, no ring, no
 * percentage, no pulsing. Linear by exemption (see motion.ts): a timer that
 * eased would lie about remaining time — paper easing stays on the panel's
 * enter/exit. Single source of advance truth: the draw's completion (or, in
 * the reduced-motion branch, its one timeout) — never two parallel timers.
 */
export function UpNextRule({
  durationMs = UP_NEXT_GRACE_MS,
  onComplete,
}: {
  durationMs?: number
  onComplete: () => void
}) {
  const reduce = useReducedMotion() ?? false
  const onCompleteRef = useRef(onComplete)
  useEffect(() => {
    onCompleteRef.current = onComplete
  })

  // Reduced motion: no drawing — a static rule plus one plain timeout, and
  // the advance itself is an instant jump.
  useEffect(() => {
    if (!reduce) return
    const handle = window.setTimeout(() => onCompleteRef.current(), durationMs)
    return () => window.clearTimeout(handle)
  }, [reduce, durationMs])

  if (reduce) return <div className="jacket-rule w-full" />

  return (
    <motion.div
      aria-hidden="true"
      className="h-px w-full origin-left bg-[rgb(var(--batyr-ink-rgb)/0.6)]"
      initial={{ scaleX: 0 }}
      animate={{ scaleX: 1 }}
      transition={{ duration: durationMs / 1000, ease: "linear" }}
      onAnimationComplete={() => onCompleteRef.current()}
    />
  )
}

/**
 * The §7.3 caption in the marginalia voice — "turning the page — stay",
 * where "stay" is the small-caps action that dismisses the turn and remains
 * indefinitely.
 */
export function UpNextCaption({ onStay }: { onStay: () => void }) {
  return (
    <p className="flex items-baseline gap-2">
      <span style={marginalia}>turning the page —</span>
      <button
        type="button"
        onClick={onStay}
        className={cn("text-batyr-ink cursor-pointer", FOCUS_RING)}
        style={chromeLabel}
      >
        stay
      </button>
    </p>
  )
}
